import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, ChevronDown } from "lucide-react";
import { SmsStatusBadge } from "./SmsStatusBadge";

const FILTERS = ["", "delivered", "sent", "queued", "failed", "undelivered", "test_mode", "skipped"];
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" }) : "");

export default function SmsHistory({ refreshKey }) {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(() => localStorage.getItem("smsHistoryOpen") === "1");
  const toggle = () => setOpen((o) => { localStorage.setItem("smsHistoryOpen", o ? "0" : "1"); return !o; });

  const load = useCallback(() => {
    api.get("/sms/messages", { params: { status, q } }).then((r) => setRows(r.data)).catch(() => {});
  }, [status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load, refreshKey]);

  return (
    <section className="border border-border/60 bg-card p-4 sm:p-8 rounded-sm" data-testid="sms-history">
      <div className={`flex flex-wrap items-center justify-between gap-4 ${open ? "mb-6" : ""}`}>
        <button type="button" onClick={toggle} aria-expanded={open} data-testid="sms-history-toggle"
          className="flex items-center gap-2 label-caps text-muted-foreground hover:text-foreground transition-colors">
          <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
          SMS history <span className="normal-case tracking-normal text-xs" data-testid="sms-history-count">({rows.length}{rows.length >= 200 ? "+" : ""})</span>
          {!open && <span className="normal-case tracking-normal text-xs text-brand">Show</span>}
        </button>
        {open && <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search client, number, text" className="flex-1 min-w-0 sm:flex-none sm:w-56" data-testid="sms-history-search" />
          <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="sms-history-filter"
            className="border border-border/70 rounded-sm px-3 py-2 bg-background text-sm outline-none focus:border-brand">
            {FILTERS.map((f) => <option key={f} value={f}>{f ? f.replace("_", " ") : "All statuses"}</option>)}
          </select>
          <Button variant="outline" size="icon" onClick={load} data-testid="sms-history-refresh" className="rounded-sm">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>}
      </div>
      {!open ? null : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="sms-history-empty">No SMS messages yet.</p>
      ) : (
        <div className="overflow-auto max-h-[560px] border-t border-border/40" data-testid="sms-history-scroll">
          <Table>
            <TableHeader className="sticky top-0 bg-card z-10">
              <TableRow>
                <TableHead>Date sent</TableHead><TableHead>Booking</TableHead><TableHead>Recipient</TableHead>
                <TableHead className="min-w-[320px]">Message</TableHead><TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`sms-row-${r.id}`}>
                  <TableCell className="whitespace-nowrap text-sm">{fmt(r.created_at)}<div className="text-xs text-muted-foreground capitalize">{r.kind}</div></TableCell>
                  <TableCell className="whitespace-nowrap text-sm">
                    {r.job_id ? <Link to={`/jobs?job=${r.job_id}`} className="text-brand hover:underline" data-testid={`sms-row-booking-${r.id}`}>{fmt(r.booking_at)}</Link> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-sm">{r.customer_name || "—"}<div className="text-xs text-muted-foreground font-mono">{r.to}</div></TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.body}</TableCell>
                  <TableCell><SmsStatusBadge status={r.status} />{r.error && <div className="text-xs text-muted-foreground mt-1 max-w-[200px]">{r.error}</div>}
                    {r.sender && <div className="text-xs text-muted-foreground mt-1" data-testid={`sms-row-sender-${r.id}`}>From {r.sender}</div>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
